import { Column, Row } from "react-table"
import { Stack, Tooltip, Typography } from "@mui/material"
import GoogleDriveIcon from "../../../assets/images/social-media/google-drive.svg"
import { ReportUploadRequestFile } from "types/reports"
import { KSpan } from "kahuna-base-react-components"
import CheckIcon from "../../../assets/images/icons/check-circle.svg"

const reportsTableColumns = (
  onClickName: (id: string) => void,
  selectedFileId: string | undefined,
  groupNames: string[],
  files: ReportUploadRequestFile[],
  selectedFileIds: string[],
  setSelectedFileIds: (fileIds: string[]) => void
): Column[] => [
  {
    Header: () => {
      return (
        <div className="flex flex-row gap-2 flex-wrap">
          {[...groupNames].map((groupName) => {
            const groupFiles = [...files].filter((file) => file.groupName === groupName).map((file) => file.fileId)
            const includesAllGroup = [...groupFiles].every((item) => selectedFileIds.includes(item))
            return (
              <div key={groupName} className="flex flex-row gap-1 items-center">
                <Typography
                  sx={{
                    color: "#1D2630"
                  }}
                >
                  {`${groupName || ""}:`}
                </Typography>
                <input
                  style={{ accentColor: "black" }}
                  type="checkbox"
                  checked={includesAllGroup}
                  onChange={() => {
                    if (includesAllGroup) {
                      const updatedFiles = [...selectedFileIds].filter((fileId) => !groupFiles.includes(fileId))
                      setSelectedFileIds(updatedFiles)
                    } else {
                      const updatedFiles: string[] = [...new Set([...selectedFileIds, ...groupFiles])]
                      setSelectedFileIds(updatedFiles)
                    }
                  }}
                />
              </div>
            )
          })}
        </div>
      )
    },
    id: "check",
    width: groupNames.length > 4 ? "8%" : "5%",
    Cell: ({ row }: { row: Row }) => {
      const fileId = (row.original as ReportUploadRequestFile).fileId
      return (
        <div style={{ minWidth: 20, maxWidth: 20 }} className="song-selection-div">
          <input
            type={"checkbox"}
            checked={selectedFileIds.includes(fileId)}
            key={`select_all_checkbox`}
            onChange={() => {
              if (selectedFileIds.includes(fileId)) {
                const updatedFilesToApprove = [...selectedFileIds].filter((file) => file !== fileId)
                setSelectedFileIds(updatedFilesToApprove)
              } else {
                const updatedFilesToApprove = [...selectedFileIds, fileId]
                setSelectedFileIds(updatedFilesToApprove)
              }
            }}
            style={{ accentColor: "black" }}
          />
        </div>
      )
    }
  },
  {
    Header: "#",
    id: "view",
    width: "5%",
    Cell: ({ row }: { row: Row }) => {
      const selected = selectedFileId === (row.original as ReportUploadRequestFile).fileId
      return <KSpan text={`${row.index + 1}`} color={selected ? "#000" : "#1D2630"} fontWeight={selected ? 600 : 400} />
    }
  },
  {
    Header: "File Name",
    id: "id",
    width: groupNames.length > 4 ? "24%" : "27%",
    Cell: ({ row }: { row: Row }) => {
      const selected = selectedFileId === (row.original as ReportUploadRequestFile).fileId
      return (
        <Stack
          direction={"row"}
          alignItems={"center"}
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-all",
            overflowWrap: "break-word",
            fontWeight: selected ? 600 : 400,
            color: selected ? "#000" : "#1D2630"
          }}
        >
          <div
            className="flex cursor-pointer"
            onClick={() => {
              onClickName((row.original as ReportUploadRequestFile).fileId)
            }}
          >
            {(row.original as ReportUploadRequestFile).name}
          </div>
        </Stack>
      )
    }
  },
  {
    Header: "Sheet",
    width: "8%",
    Cell: ({ row }: { row: Row }) => (
      <div
        style={{ borderRadius: 40, marginRight: 12, cursor: "pointer" }}
        onClick={() =>
          window.open(`https://docs.google.com/spreadsheets/d/${(row.original as ReportUploadRequestFile).fileId}`)
        }
      >
        <img style={{ width: 20 }} src={GoogleDriveIcon} alt={`google_drive_image_icon`} />
      </div>
    )
  },
  {
    Header: "Drive Folder",
    width: "9%",
    Cell: ({ row }: { row: Row }) => (
      <div
        style={{ borderRadius: 40, marginRight: 12, cursor: "pointer" }}
        onClick={() =>
          window.open(`https://drive.google.com/drive/folders/${(row.original as ReportUploadRequestFile).folderId}`)
        }
      >
        <img style={{ width: 20 }} src={GoogleDriveIcon} alt={`google_drive_image_icon`} />
      </div>
    )
  },
  {
    Header: "Distributor",
    accessor: "distributor",
    width: "5%",
    Cell: ({ row }: { row: Row }) => {
      const selected = selectedFileId === (row.original as ReportUploadRequestFile).fileId
      return (
        <Typography
          sx={{
            fontWeight: selected ? 600 : 400,
            color: selected ? "#000" : "#1D2630"
          }}
        >
          {(row.original as ReportUploadRequestFile).distributor}
        </Typography>
      )
    }
  },
  {
    Header: "Group",
    accessor: "groupName",
    width: "5%",
    Cell: ({ row }: { row: Row }) => {
      const selected = selectedFileId === (row.original as ReportUploadRequestFile).fileId
      return (
        <Tooltip
          placement="left"
          children={
            <Typography
              sx={{
                fontWeight: selected ? 600 : 400,
                color: selected ? "#000" : "#1D2630"
              }}
            >
              {(row.original as ReportUploadRequestFile)?.groupName || ""}
            </Typography>
          }
          title={
            <div className=" w-[280px] h-auto whitespace-normal">
              <KSpan
                text="Reports that share the same distributor and an identical set of columns will be shown with the same group name."
                color="#FFF"
              />
            </div>
          }
        />
      )
    }
  },
  {
    Header: "Approved",
    accessor: "approved",
    width: "5%",
    Cell: ({ row }: { row: Row }) => {
      const approved = (row.original as ReportUploadRequestFile).approved
      return approved ? (
        <Tooltip
          placement="left"
          children={
            <div
              style={{ borderRadius: 40, marginRight: 12, cursor: "pointer" }}
              onClick={() =>
                window.open(
                  `https://drive.google.com/drive/folders/${(row.original as ReportUploadRequestFile).folderId}`
                )
              }
            >
              <img style={{ width: 20 }} src={CheckIcon} alt={`check_icon`} />
            </div>
          }
          title={
            <div className=" w-[280px] h-auto whitespace-normal">
              <KSpan text="This file already approved and used to create the finalized report." color="#FFF" />
            </div>
          }
        />
      ) : (
        <div />
      )
    }
  }
]

export default reportsTableColumns
